import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, Legend } from "recharts";
import { Building2, Users, DollarSign, TrendingUp, CheckCircle } from "lucide-react";

export const ProviderPerformanceDashboard = () => {
  const { data: locations = [] } = useQuery({
    queryKey: ['performance-locations'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('locations')
        .select('id, name, city, state')
        .order('name');
      if (error) throw error;
      return data || [];
    },
  });

  const { data: checkins = [] } = useQuery({
    queryKey: ['performance-checkins'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('checkins')
        .select('id, location_id, created_at, membership_id');
      if (error) throw error;
      return data || [];
    },
  });

  const { data: memberships = [] } = useQuery({
    queryKey: ['performance-memberships'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('memberships')
        .select('id, location_id, payment_amount, pipeline_stage, created_at, status');
      if (error) throw error;
      return data || [];
    },
  });

  // Calculate performance metrics by location
  const locationPerformance = locations.map(location => {
    const locationCheckins = checkins.filter(c => c.location_id === location.id);
    const locationMemberships = memberships.filter(m => m.location_id === location.id);
    const activeMemberships = locationMemberships.filter(m => m.status === 'active');
    const revenue = locationMemberships.reduce((sum, m) => sum + (m.payment_amount || 0), 0);
    
    // Calculate conversion rate (activated memberships / total leads)
    const convertedMembers = locationMemberships.filter(m => 
      m.pipeline_stage && ['payment_processed', 'first_treatment_complete', 'next_appointment_booked'].includes(m.pipeline_stage)
    ).length;
    const totalLeads = locationMemberships.length;
    const conversionRate = totalLeads > 0 ? (convertedMembers / totalLeads) * 100 : 0;

    return {
      id: location.id,
      name: location.name,
      city: location.city,
      state: location.state,
      checkins: locationCheckins.length,
      totalMembers: locationMemberships.length,
      activeMembers: activeMemberships.length,
      revenue,
      convertedMembers,
      conversionRate: conversionRate.toFixed(1),
    };
  }).sort((a, b) => b.revenue - a.revenue);

  // Calculate totals
  const totalCheckins = checkins.length;
  const totalRevenue = memberships.reduce((sum, m) => sum + (m.payment_amount || 0), 0);
  const totalConversions = memberships.filter(m => 
    m.pipeline_stage && ['payment_processed', 'first_treatment_complete', 'next_appointment_booked'].includes(m.pipeline_stage)
  ).length;
  const avgConversionRate = memberships.length > 0 
    ? ((totalConversions / memberships.length) * 100).toFixed(1) 
    : '0.0';

  // Prepare chart data for top 10 locations
  const chartData = locationPerformance.slice(0, 10).map(loc => ({
    name: loc.name.length > 15 ? loc.name.substring(0, 15) + '...' : loc.name,
    checkins: loc.checkins,
    revenue: loc.revenue,
    conversions: loc.convertedMembers,
  }));

  // Monthly trend data (last 6 months)
  const getMonthlyTrends = () => {
    const months: { [key: string]: { checkins: number; revenue: number; conversions: number } } = {};
    const now = new Date();
    
    for (let i = 5; i >= 0; i--) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const key = date.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
      months[key] = { checkins: 0, revenue: 0, conversions: 0 };
    }

    checkins.forEach(c => {
      const date = new Date(c.created_at);
      const key = date.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
      if (months[key]) months[key].checkins++;
    });

    memberships.forEach(m => {
      const date = new Date(m.created_at);
      const key = date.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
      if (months[key]) {
        months[key].revenue += m.payment_amount || 0;
        if (m.pipeline_stage && ['payment_processed', 'first_treatment_complete', 'next_appointment_booked'].includes(m.pipeline_stage)) {
          months[key].conversions++;
        }
      }
    });

    return Object.entries(months).map(([month, data]) => ({
      month,
      ...data,
    }));
  };

  const trendData = getMonthlyTrends();

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold">Provider Performance</h2>
        <p className="text-muted-foreground">Track checkins, conversions, and revenue by location</p>
      </div>

      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Checkins</CardTitle>
            <CheckCircle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalCheckins}</div>
            <p className="text-xs text-muted-foreground">Across all locations</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Revenue</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">${totalRevenue.toLocaleString()}</div>
            <p className="text-xs text-muted-foreground">All time revenue</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Conversions</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalConversions}</div>
            <p className="text-xs text-muted-foreground">{avgConversionRate}% conversion rate</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Locations</CardTitle>
            <Building2 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{locations.length}</div>
            <p className="text-xs text-muted-foreground">With performance data</p>
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Top Locations by Revenue</CardTitle>
            <CardDescription>Revenue comparison across locations</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" tickFormatter={(value) => `$${value}`} />
                  <YAxis type="category" dataKey="name" width={100} tick={{ fontSize: 12 }} />
                  <Tooltip formatter={(value: number) => [`$${value.toLocaleString()}`, 'Revenue']} />
                  <Bar dataKey="revenue" fill="hsl(var(--primary))" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Monthly Trends</CardTitle>
            <CardDescription>Checkins and conversions over time</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={trendData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="checkins" stroke="hsl(var(--primary))" strokeWidth={2} name="Checkins" />
                  <Line type="monotone" dataKey="conversions" stroke="hsl(var(--chart-2))" strokeWidth={2} name="Conversions" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Performance Table */}
      <Card>
        <CardHeader>
          <CardTitle>Location Performance Details</CardTitle>
          <CardDescription>Detailed metrics for each location</CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Location</TableHead>
                <TableHead>City</TableHead>
                <TableHead className="text-right">Checkins</TableHead>
                <TableHead className="text-right">Members</TableHead>
                <TableHead className="text-right">Conversions</TableHead>
                <TableHead className="text-right">Conv. Rate</TableHead>
                <TableHead className="text-right">Revenue</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {locationPerformance.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                    No location data available
                  </TableCell>
                </TableRow>
              ) : (
                locationPerformance.map((loc) => (
                  <TableRow key={loc.id}>
                    <TableCell className="font-medium">{loc.name}</TableCell>
                    <TableCell>{loc.city}, {loc.state}</TableCell>
                    <TableCell className="text-right">{loc.checkins}</TableCell>
                    <TableCell className="text-right">
                      <Badge variant="outline">{loc.activeMembers} active</Badge>
                    </TableCell>
                    <TableCell className="text-right">{loc.convertedMembers}</TableCell>
                    <TableCell className="text-right">
                      <Badge variant={parseFloat(loc.conversionRate) >= 50 ? "default" : "secondary"}>
                        {loc.conversionRate}%
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right font-medium">
                      ${loc.revenue.toLocaleString()}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};
